// Review loop — the staff "Review history" list in the review drawer. One
// chronological list (newest first) across every submission for the item:
// submissions, recipient responses, returns, reopens, remote-signing re-sends,
// approvals and filings.
//
// NAMES, NEVER IDS. A staff actor arrives only as actorUserId and is resolved
// by the caller (useInternalUserLabels); an unresolved one reads
// UNRESOLVED_STAFF_NAME — never fallbackUserLabel, which prints part of the
// GUID. A recipient actor arrives already named in actorName.

import type {
  CollectionReturnComment,
  CollectionReviewEvent,
  StagedSubmissionDetail,
} from "@/api/collectionStaging";

/** Shown in place of a staff member whose id could not be resolved. */
export const UNRESOLVED_STAFF_NAME = "a Phoenix team member";
/** Shown in place of a recipient whose name did not arrive. */
export const UNNAMED_RECIPIENT = "Recipient";

/**
 * The events to list, newest first. Reads reviewHistory; a payload from a
 * server that predates it (reviewHistory absent) falls back to returnHistory,
 * whose rows are Returned events by reviewerId.
 */
export function reviewHistoryOf(
  detail: Pick<StagedSubmissionDetail, "reviewHistory" | "returnHistory">,
): CollectionReviewEvent[] {
  const events = detail.reviewHistory ?? (detail.returnHistory ?? []).map(fromReturnComment);
  return [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

function fromReturnComment(c: CollectionReturnComment): CollectionReviewEvent {
  return {
    id: c.id,
    kind: "Returned",
    stagedSubmissionId: c.stagedSubmissionId,
    actorUserId: c.reviewerId,
    actorName: null,
    note: c.note,
    createdAt: c.createdAt,
  };
}

/** Staff user ids the history needs resolved to names. */
export function staffActorIds(events: CollectionReviewEvent[]): string[] {
  return events
    .filter((e) => e.kind !== "Submitted" && e.actorUserId)
    .map((e) => e.actorUserId as string);
}

/**
 * Who did it, as a name. Submitted is the recipient's own event (actorName);
 * every other kind is a staff action named only through actorUserId.
 */
export function reviewEventActor(
  event: CollectionReviewEvent,
  staffName: (userId: string) => string | null | undefined,
): string {
  if (event.kind === "Submitted") return event.actorName?.trim() || UNNAMED_RECIPIENT;
  const resolved = event.actorUserId ? staffName(event.actorUserId)?.trim() : null;
  return resolved || UNRESOLVED_STAFF_NAME;
}

/** One history line, e.g. "Returned by Dana Reviewer: “Please sign page 2”". */
export function reviewEventText(event: CollectionReviewEvent, actor: string): string {
  const note = event.note?.trim();
  const quoted = note ? `: “${note}”` : "";
  switch (event.kind) {
    case "Submitted": {
      const filed = event.alreadyFiled ? " — the copy was already filed" : "";
      if (event.withoutChanges) return `${actor} sent it back without changes${quoted}${filed}`;
      return `${actor} ${note ? "responded" : "submitted"}${quoted}${filed}`;
    }
    case "Returned":
      return `Returned by ${actor}${quoted}`;
    case "Reopened":
      return `Reopened by ${actor}${quoted}`;
    case "ResentForRemoteSigning":
      return `Re-sent for remote signing by ${actor}`;
    case "Approved":
      return `Approved by ${actor}`;
    case "Filed": {
      const folder = event.folderName?.trim();
      return folder ? `Filed by ${actor} to ${folder}` : `Filed by ${actor}`;
    }
    default:
      return `Updated by ${actor}`;
  }
}
